import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';

const Footer = () => {
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'AMC Packages', path: '/amc-packages' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'Awards', path: '/awards' },
    { name: 'About', path: '/about' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Contact', path: '/contact' }
  ];

  const services = [
    'Regular Service',
    'Car Spa & Detailing',
    'Denting & Painting',
    'AC Repair',
    'Accident Repair',
    'Insurance Claims'
  ];

  const socialLinks = [
    { icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61559237452429', label: 'Facebook' },
    { icon: Instagram, href: 'https://www.instagram.com/carzstreetsbr?igsh=MTNuNDlpOHgxZHVzeA==', label: 'Instagram' }
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-white rounded-xl px-3 py-2 shadow-md inline-block">
                <img 
                  src="/carz-street-logo-new.jpeg" 
                  alt="Carz Street - Multibrand Workshop" 
                  className="h-14 w-auto object-contain"
                />
              </div>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Drive in with problems & Drive out with peace of mind. Your trusted partner for premium car service and maintenance.
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  data-testid={`social-${social.label.toLowerCase()}`}
                  className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors duration-300"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <social.icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h4 className="text-lg font-bold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-red-400 transition-colors duration-300 flex items-center group"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-red-600 transition-all duration-300 mr-0 group-hover:mr-2"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4 className="text-lg font-bold mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index} className="text-gray-400 hover:text-red-400 transition-colors duration-300 cursor-pointer">
                  {service}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h4 className="text-lg font-bold mb-6">Our Locations</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-400">
                  <span className="text-white font-semibold block mb-1">Head Office &mdash; Ahmedabad</span>
                  Carz Street, PRL colony, Behind Minotti Furniture, Sindhu Bhavan Road, Bodakdev, Ahmedabad-380054 Gujarat
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-400">
                  <span className="text-white font-semibold block mb-1">Indore Branch</span>
                  Plot no. 30, New Bijalpur Main Road, Indore 452012, MP<br />
                  <a href="tel:+919630541958" className="hover:text-red-400 transition-colors">Contact: +91 96305 41958</a>
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-400">
                  <span className="text-red-400 font-semibold block mb-1">Coming Soon &mdash; Ahmedabad (Tragad)</span>
                  Carz Street, Opp. Hillock Hotel, Next to Shri Hari Dreamland, Ahmedabad (Tragad) 382470 Gujarat
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div className="text-gray-400">
                  <a href="tel:+919925025351" className="hover:text-red-400 transition-colors block">
                    +91 99250 25351
                  </a>
                  <a href="tel:+918866689002" className="hover:text-red-400 transition-colors block text-sm mt-1">
                    Customer Care: +91 88666 89002
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-gray-400 space-y-1">
                  <a href="mailto:info@carzstreet.com" className="hover:text-red-400 transition-colors block">
                    info@carzstreet.com
                  </a>
                  <a href="mailto:service@carzstreet.com" className="hover:text-red-400 transition-colors block">
                    service@carzstreet.com
                  </a>
                  <a href="mailto:insurance@carzstreet.com" className="hover:text-red-400 transition-colors block">
                    insurance@carzstreet.com
                  </a>
                  <a href="mailto:accounts@carzstreet.com" className="hover:text-red-400 transition-colors block">
                    accounts@carzstreet.com
                  </a>
                </div>
              </li>
            </ul>
          </motion.div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Carz Street. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-red-400 transition-colors">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-red-400 transition-colors">Terms of Service</a>
              <a href="#" className="text-gray-400 hover:text-red-400 transition-colors">Cookie Policy</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;